import type { Directive } from 'vue'

interface DefaultConfig {
  background?: string
  spinnerColor?: string
  spinnerSize?: string
  zIndex?: number
  isLoading?: boolean
  text?: string
  textColor?: string // 文案颜色
  textSize?: string // 文案大小
}

interface ElementLoading extends HTMLElement {
  _loadingElement?: HTMLDivElement
}

const DEFAULT_CONFIG: Required<Omit<DefaultConfig, 'isLoading'>> = {
  background: '#0003',
  spinnerColor: '#9377ff',
  spinnerSize: '20px',
  zIndex: 99,
  text: '',
  textColor: '#9377ff',
  textSize: '14px',
}

function isObject(val: unknown): val is Record<string, any> {
  return val !== null && typeof val === 'object'
}

// 全局注入动画样式（只执行一次）
function ensureGlobalStyle() {
  if (!document.getElementById('ld-loading-style')) {
    const style = document.createElement('style')
    style.id = 'ld-loading-style'
    style.textContent = `
      @keyframes ld-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `
    document.head.appendChild(style)
  }
}

function createLoadingElement(config: DefaultConfig) {
  ensureGlobalStyle()

  const wrapper = document.createElement('div')
  wrapper.style.cssText = `
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${config.background ?? DEFAULT_CONFIG.background};
    z-index: ${config.zIndex ?? DEFAULT_CONFIG.zIndex};
    gap:10px;
  `

  const spinner = document.createElement('div')
  spinner.style.cssText = `
    width: ${config.spinnerSize ?? DEFAULT_CONFIG.spinnerSize};
    height: ${config.spinnerSize ?? DEFAULT_CONFIG.spinnerSize};
    border: 2px solid ${config.spinnerColor ?? DEFAULT_CONFIG.spinnerColor};
    border-top-color: transparent;
    border-radius: 50%;
    animation: ld-spin 0.9s linear infinite;
  `

  // 3. 文案元素 (添加 class 方便查找)
  const textEl = document.createElement('p')
  textEl.className = 'ld-text'
  textEl.textContent = config.text ?? DEFAULT_CONFIG.text
  textEl.style.cssText = `
    color: ${config.textColor ?? DEFAULT_CONFIG.textColor};
    font-size: ${config.textSize ?? DEFAULT_CONFIG.textSize};
    line-height: 1.5;
    white-space: nowrap;
    display: ${config.text ? 'block' : 'none'}; /* 有字才显示 */
  `

  wrapper.appendChild(spinner)
  wrapper.appendChild(textEl)
  return wrapper
}

const loadingDirective: Directive<ElementLoading, boolean | DefaultConfig> = {
  mounted(el, binding) {
    const config = isObject(binding.value)
      ? { ...DEFAULT_CONFIG, ...binding.value }
      : { ...DEFAULT_CONFIG, isLoading: binding.value }

    const showLoading = !!config.isLoading

    // 保证容器可定位
    if (getComputedStyle(el).position === 'static') {
      el.style.position = 'relative'
    }

    el._loadingElement = createLoadingElement(config)
    if (showLoading) {
      el.appendChild(el._loadingElement)
    }
  },

  updated(el, binding) {
    const config = isObject(binding.value)
      ? { ...DEFAULT_CONFIG, ...binding.value }
      : { ...DEFAULT_CONFIG, isLoading: binding.value }

    const showLoading = !!config.isLoading

    // 如果没加载过元素则创建
    if (!el._loadingElement) {
      el._loadingElement = createLoadingElement(config)
    }

    // 更新样式（若配置变更）
    const spinner = el._loadingElement.querySelector('div')
    if (spinner) {
      spinner.style.borderColor = config.spinnerColor
      spinner.style.borderTopColor = 'transparent'
      spinner.style.width = config.spinnerSize
      spinner.style.height = config.spinnerSize
    }
    el._loadingElement.style.background = config.background

    // 控制显示 / 隐藏
    if (showLoading) {
      if (!el.contains(el._loadingElement)) {
        el.appendChild(el._loadingElement)
      }
    }
    else {
      el.contains(el._loadingElement) && el.removeChild(el._loadingElement)
    }
  },

  unmounted(el) {
    if (el._loadingElement && el.contains(el._loadingElement)) {
      el.removeChild(el._loadingElement)
    }
  },
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('LdLoading', loadingDirective)
})
